import React, { Fragment, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useMediaQuery } from '@mantine/hooks';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: string;
  subtitle?: string; // Alias for description
  children: React.ReactNode;
  size?: ModalSize;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  footer?: React.ReactNode;
  initialFocus?: React.MutableRefObject<HTMLElement | null>;
}

const sizeStyles: Record<ModalSize, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  full: 'max-w-[calc(100vw-2rem)] sm:h-[calc(100vh-2rem)]',
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  subtitle,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  footer,
  initialFocus,
}) => {
  // Use subtitle as alias for description
  const modalDescription = description || subtitle;
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const isMobile = useMediaQuery('(max-width: 640px)');

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={closeOnOverlayClick ? onClose : () => {}}
        initialFocus={initialFocus || closeButtonRef}
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal Container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className={clsx(
            'flex min-h-full',
            isMobile ? 'items-end' : 'items-center justify-center p-4'
          )}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  'flex w-full transform flex-col overflow-hidden bg-white shadow-xl transition-all',
                  isMobile ? 'max-h-[92vh] rounded-t-2xl' : 'max-h-[calc(100vh-2rem)] rounded-2xl',
                  sizeStyles[size]
                )}
              >
                {/* Header */}
                {(title || showCloseButton) && (
                  <div className="flex flex-shrink-0 items-start justify-between p-6 pb-0">
                    <div>
                      {title && (
                        <Dialog.Title className="text-lg font-semibold text-neutral-900">
                          {title}
                        </Dialog.Title>
                      )}
                      {modalDescription && (
                        <Dialog.Description className="mt-1 text-sm text-neutral-500">
                          {modalDescription}
                        </Dialog.Description>
                      )}
                    </div>
                    {showCloseButton && (
                      <button
                        ref={closeButtonRef}
                        type="button"
                        onClick={onClose}
                        className={clsx(
                          'rounded-lg p-2 text-neutral-400',
                          'hover:bg-neutral-100 hover:text-neutral-600',
                          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                          'transition-colors'
                        )}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                )}

                {/* Body */}
                <div className="min-h-0 flex-1 overflow-y-auto p-6">{children}</div>

                {/* Footer */}
                {footer && (
                  <div className="flex flex-shrink-0 items-center justify-end gap-3 border-t border-neutral-200 bg-neutral-50 px-6 py-4">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

Modal.displayName = 'Modal';

// Confirmation Modal preset
export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  isLoading = false,
}) => {
  const variantStyles = {
    danger: 'bg-error-600 hover:bg-error-700 focus:ring-error-500',
    warning: 'bg-warning-600 hover:bg-warning-700 focus:ring-warning-500',
    info: 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500',
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <p className="text-neutral-600">{message}</p>
      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={onClose}
          disabled={isLoading}
          className="btn-secondary"
        >
          {cancelText}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isLoading}
          className={clsx(
            'btn text-white',
            variantStyles[variant],
            isLoading && 'opacity-50 cursor-not-allowed'
          )}
        >
          {isLoading ? 'Loading...' : confirmText}
        </button>
      </div>
    </Modal>
  );
};

ConfirmModal.displayName = 'ConfirmModal';

export default Modal;
